// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Paper, Stack, Title } from '@mantine/core';
import type { DiagnosticReport, Patient, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { CLOUDPAT_HSAT_CODING, HSAT_PATIENT_APPROVAL_PENDING_TAG } from '@polarissleep/shared';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { HsatOrderBanner } from '../../components/HsatOrderBanner';
import { useIntakeQuestionnaireOutstanding } from '../../hooks/useIntakeQuestionnaireOutstanding';
import { useMyChartData } from '../../hooks/useMyChartData';
import { DiagnosesSection } from './DiagnosesSection';
import { DoctorsNotesSection } from './DoctorsNotesSection';
import { MedicationsSection } from './MedicationsSection';
import { PrescriptionsSection } from './PrescriptionsSection';
import { SupplementalInformationSection } from './SupplementalInformationSection';
import { TestsAndLabsSection } from './TestsAndLabsSection';

export function MyChartPage(): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const chart = useMyChartData();
  const { outstanding: intakeQuestionnaireDeferred } = useIntakeQuestionnaireOutstanding();
  const [pendingHsatOrder, setPendingHsatOrder] = useState<ServiceRequest | undefined>();
  const [orderedServiceRequest, setMostRecentWatchPATServiceRequest] = useState<ServiceRequest | undefined>();
  const [diagnosticReports, setDiagnosticReports] = useState<DiagnosticReport[]>([]);

  useEffect(() => {
    if (!profile?.id) {
      return;
    }
    medplum
      .searchOne('ServiceRequest', {
        subject: `Patient/${profile.id}`,
        _tag: `${HSAT_PATIENT_APPROVAL_PENDING_TAG.system}|${HSAT_PATIENT_APPROVAL_PENDING_TAG.code}`,
        _sort: '-_lastUpdated',
      })
      .then(setPendingHsatOrder)
      .catch(console.error);
    medplum
      .searchOne('ServiceRequest', {
        subject: `Patient/${profile.id}`,
        code: CLOUDPAT_HSAT_CODING.code,
        _sort: '-_lastUpdated',
      })
      .then(setMostRecentWatchPATServiceRequest)
      .catch(console.error);
    medplum
      .searchResources('DiagnosticReport', {
        subject: `Patient/${profile.id}`,
        _sort: '-_lastUpdated',
        _count: '100',
      })
      .then((results) => setDiagnosticReports(Array.from(results) as DiagnosticReport[]))
      .catch(console.error);
  }, [medplum, profile?.id]);

  return (
    <Stack gap="lg" p="md">
      {pendingHsatOrder && <HsatOrderBanner onOrderNow={() => navigate(`/order-hsat/${pendingHsatOrder.id}`)} />}
      <Paper withBorder radius="md" shadow="sm" px="xl" py="md">
        <Title order={1} size="2.25rem" fw={700}>
          My Chart
        </Title>
      </Paper>
      <SupplementalInformationSection
        intakeComposition={chart.intakeComposition}
        hsatReports={chart.hsatReports}
        loading={chart.loading}
        intakeQuestionnaireDeferred={intakeQuestionnaireDeferred}
      />
      <DoctorsNotesSection />
      <TestsAndLabsSection
        hsatOrders={chart.hsatOrders}
        loading={chart.loading}
        orderedServiceRequest={orderedServiceRequest}
        diagnosticReports={diagnosticReports}
      />
      <DiagnosesSection diagnoses={chart.conditions} loading={chart.loading} />
      <PrescriptionsSection
        prescriptions={chart.prescriptions}
        prescriptionDocuments={chart.prescriptionDocuments}
        loading={chart.loading}
      />
      <MedicationsSection
        medications={chart.medications}
        loading={chart.loading}
        onChanged={chart.setMedications}
        onRefresh={chart.refresh}
      />
    </Stack>
  );
}
